"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Cog, Cpu, Code as Code2, Radio, Gamepad2, Bot, Target, Sparkles, Info, Globe, ArrowRight, ChevronDown, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
interface SkillNode {
  id: string;
  step: number;
  titleVi: string;
  titleEn: string;
  descVi: string;
  descEn: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
interface SoftSkill {
  id: string;
  titleVi: string;
  titleEn: string;
  categoryVi: string;
  categoryEn: string;
}
const PRIMARY_CHAIN: SkillNode[] = [{
  id: "mechanical",
  step: 1,
  titleVi: "Tư duy cơ khí",
  titleEn: "Mechanical Thinking",
  descVi: "Cấu trúc, khớp nối, chuyển động & lực truyền động",
  descEn: "Structures, joints, motion & drive mechanics",
  icon: Cog
}, {
  id: "computational",
  step: 2,
  titleVi: "Tư duy tính toán",
  titleEn: "Computational Thinking",
  descVi: "Phân rã vấn đề, nhận diện quy luật & thuật toán",
  descEn: "Decomposition, pattern recognition & algorithms",
  icon: Cpu
}, {
  id: "programming",
  step: 3,
  titleVi: "Lập trình",
  titleEn: "Programming",
  descVi: "Kéo thả khối lệnh đến mã nguồn chuyên nghiệp",
  descEn: "From block-based coding to text languages",
  icon: Code2
}, {
  id: "sensing",
  step: 4,
  titleVi: "Cảm biến",
  titleEn: "Sensing",
  descVi: "Thu thập dữ liệu môi trường, sóng âm, màu sắc",
  descEn: "Environment data, ultrasonic & optical sensing",
  icon: Radio
}, {
  id: "control",
  step: 5,
  titleVi: "Điều khiển robot",
  titleEn: "Robot Control",
  descVi: "Điều hướng động cơ, phản hồi vòng kín & độ chính xác",
  descEn: "Motor navigation, closed-loop feedback & precision",
  icon: Gamepad2
}, {
  id: "autonomous",
  step: 6,
  titleVi: "Robot tự hành",
  titleEn: "Autonomous Robotics",
  descVi: "Tự ra quyết định linh hoạt không qua điều khiển tay",
  descEn: "Real-time decision making without manual control",
  icon: Bot
}, {
  id: "strategy",
  step: 7,
  titleVi: "Chiến thuật nhiệm vụ",
  titleEn: "Mission Strategy",
  descVi: "Tối ưu hóa thời gian, tài nguyên & giải bài toán thi đấu",
  descEn: "Time, resource optimization & arena competition strategy",
  icon: Target
}];
const SOFT_SKILLS: SoftSkill[] = [{
  id: "problem-solving",
  titleVi: "Giải quyết vấn đề",
  titleEn: "Problem Solving",
  categoryVi: "Tư duy",
  categoryEn: "Mindset"
}, {
  id: "debugging",
  titleVi: "Gỡ lỗi",
  titleEn: "Debugging",
  categoryVi: "Kỹ thuật",
  categoryEn: "Technical"
}, {
  id: "teamwork",
  titleVi: "Làm việc nhóm",
  titleEn: "Teamwork",
  categoryVi: "Xã hội",
  categoryEn: "Social"
}, {
  id: "presentation",
  titleVi: "Thuyết trình",
  titleEn: "Presentation",
  categoryVi: "Giao tiếp",
  categoryEn: "Communication"
}, {
  id: "engineering-thinking",
  titleVi: "Tư duy kỹ thuật",
  titleEn: "Engineering Thinking",
  categoryVi: "Cốt lõi",
  categoryEn: "Core"
}];
export default function SkillsDevelopmentMatrix() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    // Read initial language from localStorage if available
    try {
      const stored = localStorage.getItem("roboto_lang") as "vi" | "en" | null;
      if (stored === "vi" || stored === "en") {
        setLang(stored);
      }
    } catch {
      // Ignore fallback errors
    }
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail && (customEvent.detail === "vi" || customEvent.detail === "en")) {
        setLang(customEvent.detail);
      } else {
        try {
          const updated = localStorage.getItem("roboto_lang") as "vi" | "en" | null;
          if (updated === "vi" || updated === "en") {
            setLang(updated);
          }
        } catch {
          // Ignore
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const handleToggleLang = (newLang: "vi" | "en") => {
    setLang(newLang);
    try {
      localStorage.setItem("roboto_lang", newLang);
      window.dispatchEvent(new CustomEvent("roboto-lang-change", {
        detail: newLang
      }));
    } catch {
      // Ignore
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.07
      }
    }
  };
  const nodeVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="skillsdevelopmentmatrix" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      {/* Decorative backdrop shapes & robotics technical background */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden select-none">
        {/* Supporting robotics photography layer */}
        <div className="absolute inset-0 z-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_skills_development_bg.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_skills_development_bg.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_skills_development_bg.png-wvc-sizes" alt="Robotics technical development lab environment" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-12 mix-blend-multiply filter contrast-125" />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        </div>

        {/* Blueprint technical coordinate grid overlay */}
        <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(hsl(222_47%_20%)_1px,transparent_1px)] [background-size:24px_24px]" />

        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-lg bg-secondary/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-lg bg-primary/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[20rem] bg-accent/5 rounded-lg blur-3xl pointer-events-none" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block with Language Toggle */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="outline" className="border-secondary/40 text-secondary bg-secondary/10 px-3 py-1 rounded-lg text-xs font-semibold tracking-wide uppercase">
              <Sparkles className="size-3.5 mr-1" />
              {lang === "vi" ? "Lộ trình năng lực STEM" : "STEM Competency Matrix"}
            </Badge>

            {/* Quick Language Toggle Control */}
            <div className="inline-flex items-center rounded-lg border border-border bg-card p-0.5 shadow-2xs">
              <Button type="button" variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => handleToggleLang("vi")} className="h-6 px-2 text-[11px] font-medium rounded-md">
                VI
              </Button>
              <Button type="button" variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => handleToggleLang("en")} className="h-6 px-2 text-[11px] font-medium rounded-md">
                EN
              </Button>
            </div>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground font-serif">
            {lang === "vi" ? "HỌC SINH SẼ PHÁT TRIỂN GÌ?" : "WHAT WILL STUDENTS DEVELOP?"}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl">
            {lang === "vi" ? "Hệ thống năng lực chuẩn kỹ thuật quốc tế được tích hợp đồng bộ theo từng cấp độ học tập tại ROBOTO." : "An international engineering-grade framework sequentially integrated across all ROBOTO learning levels."}
          </p>
        </div>

        {/* Primary Competency Horizontal Chain (Stacking Vertically on Mobile) */}
        <div className="mb-14">
          <div className="flex items-center justify-between mb-4 px-1">
            <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-1.5">
              <span className="size-2 rounded-lg bg-secondary inline-block" />
              {lang === "vi" ? "Tiến trình kỹ thuật cốt lõi (1 - 7)" : "Core Technical Chain (1 - 7)"}
            </span>
            <span className="text-xs text-muted-foreground hidden sm:inline-block">
              {lang === "vi" ? "Tăng dần theo cấp độ bài học" : "Progressive difficulty tier"}
            </span>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="grid grid-cols-1 md:grid-cols-7 gap-3 lg:gap-2 relative">
            {PRIMARY_CHAIN.map((node, index) => {
            const IconComponent = node.icon;
            const isLast = index === PRIMARY_CHAIN.length - 1;
            return <div key={node.id} className="relative flex flex-col group">
                  <motion.div variants={nodeVariants} className="h-full">
                    <Card className="h-full border border-border/80 bg-card/90 backdrop-blur-xs transition-[color,background-color,border-color,box-shadow] duration-300 hover:border-secondary/60 hover:shadow-md relative z-10 flex flex-col justify-between overflow-hidden">
                      {/* Top Step Accent Header */}
                      <div className="p-3.5 pb-2 border-b border-border/40 flex items-center justify-between bg-muted/30">
                        <span className="text-[10px] font-mono font-bold text-muted-foreground bg-background px-1.5 py-0.5 rounded border border-border/50">
                          0{node.step}
                        </span>
                        <div className="p-1 rounded-md bg-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors">
                          <IconComponent className="size-4" />
                        </div>
                      </div>

                      {/* Content */}
                      <CardContent className="p-3.5 pt-3 flex-1 flex flex-col justify-between">
                        <div>
                          <h3 className="text-xs sm:text-sm font-semibold text-foreground leading-snug group-hover:text-primary transition-colors">
                            {lang === "vi" ? node.titleVi : node.titleEn}
                          </h3>
                          <p className="mt-1.5 text-[11px] text-muted-foreground leading-relaxed">
                            {lang === "vi" ? node.descVi : node.descEn}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Desktop Horizontal Arrow Connector */}
                  {!isLast && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-20 pointer-events-none items-center justify-center">
                      <div className="size-6 rounded-lg bg-background border border-border shadow-2xs flex items-center justify-center text-muted-foreground group-hover:text-secondary group-hover:border-secondary transition-colors">
                        <ArrowRight className="size-3" />
                      </div>
                    </div>}

                  {/* Mobile Vertical Arrow Connector */}
                  {!isLast && <div className="flex md:hidden justify-center my-1 z-20 pointer-events-none">
                      <div className="size-6 rounded-lg bg-background border border-border shadow-2xs flex items-center justify-center text-muted-foreground">
                        <ChevronDown className="size-3.5" />
                      </div>
                    </div>}
                </div>;
          })}
          </motion.div>
        </div>

        {/* Secondary Grid: Soft & Transversal Skills */}
        <div className="mb-12 bg-card/60 rounded-2xl p-6 sm:p-8 border border-border/60 shadow-2xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-foreground flex items-center gap-2">
                <CheckCircle2 className="size-5 text-secondary" />
                {lang === "vi" ? "Bộ kỹ năng bổ trợ & Kỹ năng mềm" : "Transversal & Soft Skills"}
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                {lang === "vi" ? "Rèn luyện song song qua từng đồ án và cuộc thi robotics" : "Developed in parallel through every robotics project and contest"}
              </p>
            </div>
            <Badge variant="secondary" className="w-fit text-xs px-2.5 py-0.5 font-medium">
              5 {lang === "vi" ? "Kỹ năng thiết yếu" : "Essential Skills"}
            </Badge>
          </div>

          <div className="flex flex-wrap gap-3">
            {SOFT_SKILLS.map(skill => <motion.div key={skill.id} whileHover={{
            y: -2,
            scale: 1.02
          }} transition={{
            duration: 0.15
          }} className="flex-1 min-w-[140px] sm:min-w-[180px]">
                <div className="flex items-center gap-2.5 p-3 rounded-xl border border-border/80 bg-background hover:border-secondary/80 hover:bg-secondary/5 transition-[color,background-color,border-color,box-shadow] duration-200 cursor-default shadow-2xs group">
                  <div className="size-2 rounded-lg bg-secondary group-hover:scale-125 transition-transform" />
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors">
                      {lang === "vi" ? skill.titleVi : skill.titleEn}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {lang === "vi" ? skill.categoryVi : skill.categoryEn}
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Clarifying Note Banner */}
        <div className="max-w-3xl mx-auto rounded-xl border border-border/80 bg-muted/40 p-4 sm:p-5 flex items-start gap-3.5">
          <div className="p-2 rounded-lg bg-background border border-border/60 text-primary shrink-0 mt-0.5 shadow-2xs">
            <Info className="size-4" />
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground mr-1">
              {lang === "vi" ? "Lưu ý:" : "Note:"}
            </span>
            {lang === "vi" ? "Đây là tiến trình năng lực xuyên suốt toàn bộ lộ trình, không phải tất cả kỹ năng đều xuất hiện ở mọi khóa học." : "This is a competency progression across the whole pathway — not every skill appears in every single course."}
          </div>
        </div>
      </div>
    </section>;
}